import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import * as d3 from "d3";
import clsx from "clsx";
import { useDragToScroll } from "../../../hooks/useDragToScroll";
import { ReportHRGraphData } from "./ReportHRGraph";
import "./ScrollableHRGraph.scss";

interface Props {
    className?: string;
    data: ReportHRGraphData[];
    containerWidth: number;
    inPrintMode?: boolean;
    chartHeight?: number;
}

const ScrollableReportHRGraph: React.FC<Props> = ({
    className = "",
    data,
    containerWidth,
    inPrintMode = false,
    chartHeight = 300,
}) => {
    const { ref: scrollRef, isDragging } = useDragToScroll();
    const barsSvgRef = useRef<SVGSVGElement | null>(null);
    const yAxisSvgRef = useRef<SVGSVGElement | null>(null);

    const [contentEndReached, setContentEndReached] = useState(true);
    const [fitsContainer, setFitsContainer] = useState(false);
    const [atStart, setAtStart] = useState(true);

    const updateContentEnd = useCallback((el: HTMLDivElement | null) => {
        if (!el) return;
        if (barsSvgRef.current) {
            const svgW = barsSvgRef.current.getBoundingClientRect().width;
            const contW = el.getBoundingClientRect().width;
            setFitsContainer(svgW <= contW + 0.5);
        }

        //right-edge shadow flag
        const hiddenRight = el.scrollWidth - el.clientWidth - el.scrollLeft;
        setContentEndReached(hiddenRight <= 0);
        setAtStart(el.scrollLeft === 0);
    }, []);

    useLayoutEffect(() => {
        //run after each re-layout / data change so initial state is right
        updateContentEnd(scrollRef.current);
    }, [updateContentEnd, data, containerWidth, scrollRef]);

    //keep flag updated on native scroll
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) =>
        updateContentEnd(e.currentTarget);

    //keep flag updated while drag-scrolling (hook mutates scrollLeft)
    useEffect(() => {
        const el = scrollRef.current;
        if (!el || !isDragging) return;

        const move = () => updateContentEnd(el);
        window.addEventListener("pointermove", move);
        return () => window.removeEventListener("pointermove", move);
    }, [isDragging, updateContentEnd]);

    useEffect(() => {
        if (!data.length || !barsSvgRef.current || !yAxisSvgRef.current) return;

        const margin = { top: 20, right: 10, bottom: 60, left: 35 };
        const barWidth = 12;
        const svgH = chartHeight;
        const svgW = barWidth * data.length;

        // scales
        const vis = data.filter((d) => d.high || d.low);
        const yMax =
            Math.ceil((d3.max(vis, (d) => d.high)! + 5 - 1e-3) / 5) * 5;
        const yMin = Math.floor((d3.min(vis, (d) => d.low)! - 5) / 5) * 5;
        const y = d3
            .scaleLinear()
            .domain([yMin, yMax])
            .range([svgH - margin.bottom, margin.top]);

        // fixed Y-axis
        const ySvg = d3.select(yAxisSvgRef.current);
        ySvg.selectAll("*").remove();
        ySvg.attr("width", margin.left).attr("height", svgH);
        ySvg.append("g")
            .attr("transform", `translate(${margin.left - 5},0)`)
            .call(d3.axisLeft(y).ticks(5));

        // scrollable bars + X-axis
        const svg = d3.select(barsSvgRef.current);
        svg.selectAll("*").remove();
        svg.attr("width", svgW).attr("height", svgH);

        const x = d3
            .scaleBand<string>()
            .domain(data.map((_, i) => i + ""))
            .range([0, svgW - margin.right])
            .padding(0.1);

        svg.selectAll(".h-grid")
            .data(y.ticks(5))
            .enter()
            .append("line")
            .attr("class", "h-grid")
            .attr("x1", 0)
            .attr("x2", svgW - margin.right)
            .attr("y1", (d) => y(d))
            .attr("y2", (d) => y(d))
            .attr("stroke", "#ccc")
            .attr("pointer-events", "none");

        const tickIndices = data
            .map((_, i) => i)
            .filter((i) => (i + 1) % 5 === 0)
            .map(String);

        const xAxisG = svg
            .append("g")
            .attr("transform", `translate(0,${svgH - margin.bottom})`)
            .call(
                d3
                    .axisBottom(x)
                    .tickValues(tickIndices)
                    .tickFormat((d) =>
                        d3.timeFormat("%-m/%-d/%y")(new Date(data[+d].date)),
                    )
                    .tickSizeOuter(0),
            );

        xAxisG
            .selectAll("text")
            .style("user-select", "none")
            .style("-webkit-user-select", "none")
            .style("pointer-events", "none");

        data.forEach((d, i) => {
            const xpos = x(i + "");
            if (xpos === undefined) return;
            let yHigh = y(d.high);
            const yLow = y(d.low);
            if (d.high === d.low && d.high !== 0) yHigh -= 10;

            svg.append("line")
                .attr("x1", xpos + x.bandwidth() / 2)
                .attr("x2", xpos + x.bandwidth() / 2)
                .attr("y1", margin.top)
                .attr("y2", svgH - margin.bottom)
                .attr("stroke", "#ccc");

            svg.append("rect")
                .attr("x", xpos)
                .attr("y", yHigh)
                .attr("width", x.bandwidth())
                .attr("height", Math.max(yLow - yHigh, 0))
                .attr("fill", "#59A6C4")
                .attr("rx", 4);
        });

        const containerEl = scrollRef.current;
        if (containerEl) {
            requestAnimationFrame(() => {
                // only if wider than viewport
                if (containerEl.scrollWidth > containerEl.clientWidth) {
                    containerEl.scrollLeft =
                        containerEl.scrollWidth - containerEl.clientWidth;
                    updateContentEnd(containerEl);
                }
            });
        }
    }, [data, containerWidth, chartHeight, scrollRef, updateContentEnd]);

    return (
        <div className="ScrollableReportHRGraphWrap">
            <svg ref={yAxisSvgRef} className="ScrollableReportHRGraphYAxis" />
            <div
                ref={scrollRef}
                className={clsx(
                    "ScrollableReportHRGraph",
                    className,
                    { "is-dragging": isDragging },
                    { "fits-container": fitsContainer },
                )}
                onScroll={handleScroll}
            >
                <svg ref={barsSvgRef} className="ScrollableReportHRGraphSvg" />
            </div>

            {/* left fade */}
            <div
                className={clsx(
                    "scroll-shadow",
                    "left",
                    { visible: !atStart },
                    { "fits-container": fitsContainer },
                )}
            />

            {/*right fade */}
            <div
                className={clsx(
                    "scroll-shadow",
                    "right",
                    { visible: !contentEndReached },
                    { "fits-container": fitsContainer },
                )}
            />
        </div>
    );
};

export default ScrollableReportHRGraph;
